import React, { useEffect, useMemo, useState } from "react";
import { Alert, Card, Modal, Select, Space, Spin, Typography, message } from "antd";
import { UserSwitchOutlined } from "@ant-design/icons";
import { API_BASE } from "./config/api";
import { clearDistributorSession, getDistributorToken } from "./config/auth";

const { Title, Text } = Typography;

const isAuthFailure = (response, data) => {
  const code = String(data?.code || "");
  if (
    response.status === 401 ||
    ["AUTH_TOKEN_REQUIRED", "INVALID_AUTH_TOKEN", "AUTH_TOKEN_EXPIRED", "AUTH_SESSION_IDLE_EXPIRED"].includes(code)
  ) {
    clearDistributorSession();
    window.location.replace("/login");
    return true;
  }
  return false;
};

export default function ClientLoginManagement() {
  const [modal, modalContextHolder] = Modal.useModal();
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [changing, setChanging] = useState(false);
  const [selectedId, setSelectedId] = useState(undefined);
  const [error, setError] = useState("");

  const loadClients = async () => {
    setLoading(true);
    setError("");
    try {
      const token = getDistributorToken();
      if (!token) {
        clearDistributorSession();
        window.location.replace("/login");
        return;
      }

      const response = await fetch(`${API_BASE}/distributor_client_login_list.php`, {
        method: "GET",
        headers: { "X-KG-Distributor-Authorization": `Bearer ${token}` },
      });
      const data = await response.json().catch(() => ({}));
      if (isAuthFailure(response, data)) return;
      if (!response.ok || data.status !== "ok") {
        throw new Error(data.message || "Unable to load clients.");
      }
      setClients(Array.isArray(data.clients) ? data.clients : []);
    } catch (e) {
      setError(e.message || "Unable to load clients.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { loadClients(); }, []);

  const options = useMemo(
    () => clients.map((c) => ({
      value: c.id,
      label: `${c.client_name || "Client"} (${c.pan})`,
    })),
    [clients]
  );

  const toggleClient = (id) => {
    const client = clients.find((c) => String(c.id) === String(id));
    if (!client) return;

    const isActive = client.account_status === "ACTIVE";
    const action = isActive ? "Block Login" : "Activate Login";
    const nextStatus = isActive ? "BLOCKED" : "ACTIVE";

    modal.confirm({
      title: `${action} of ${client.client_name || "Client"} (${client.pan})?`,
      content: isActive
        ? "The client's current login sessions will also be ended."
        : "The client will be allowed to login again.",
      okText: "Yes",
      cancelText: "No",
      okButtonProps: isActive ? { danger: true } : {},
      centered: true,
      onCancel: () => setSelectedId(undefined),
      onOk: async () => {
        setChanging(true);
        try {
          const token = getDistributorToken();
          const response = await fetch(`${API_BASE}/distributor_client_login_status.php`, {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
              "X-KG-Distributor-Authorization": `Bearer ${token}`,
            },
            body: JSON.stringify({ client_login_id: client.id, account_status: nextStatus }),
          });
          const data = await response.json().catch(() => ({}));
          if (isAuthFailure(response, data)) return;
          if (!response.ok || data.status !== "ok") {
            throw new Error(data.message || "Unable to change login status.");
          }

          setClients((items) =>
            items.map((item) =>
              String(item.id) === String(client.id)
                ? { ...item, account_status: data.account_status }
                : item
            )
          );
          message.success(
            data.account_status === "BLOCKED"
              ? `Login blocked for ${client.client_name || "Client"} (${client.pan}).`
              : `Login activated for ${client.client_name || "Client"} (${client.pan}).`
          );
        } catch (e) {
          message.error(e.message || "Unable to change login status.");
        } finally {
          setChanging(false);
          setSelectedId(undefined);
        }
      },
    });
  };

  return (
    <>
      {modalContextHolder}
      <div style={{ maxWidth: 760, margin: "0 auto", padding: 20 }}>
      <Card>
        <Space direction="vertical" size={6} style={{ width: "100%", marginBottom: 20 }}>
          <Title level={3} style={{ margin: 0 }}>
            <UserSwitchOutlined /> Client Login Management
          </Title>
          <Text type="secondary">
            Select a client to block or activate MyWealth login.
          </Text>
        </Space>

        {error && <Alert type="error" showIcon message={error} style={{ marginBottom: 16 }} />}

        {loading ? (
          <div style={{ padding: 28, textAlign: "center" }}><Spin /></div>
        ) : (
          <Select
            showSearch
            allowClear
            size="large"
            style={{ width: "100%" }}
            placeholder="Select Client Name (PAN)"
            value={selectedId}
            options={options}
            disabled={changing}
            optionFilterProp="label"
            onChange={(value) => {
              setSelectedId(value);
              if (value != null) toggleClient(value);
            }}
            notFoundContent="No clients found."
          />
        )}
      </Card>
      </div>
    </>
  );
}
